import { Card } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import './flock-profile-card.scss';
import { FlockProfileChart } from './flock-profile-chart';
import { FlockProfileChartMobile } from './flock-profile-chart-mobile';
import { FlockProfileCardModel } from './flock-profile-types';

type Props = {
  model: FlockProfileCardModel;
  chartMode?: 'responsive' | 'desktop';
  desktopChartWidth?: number;
  showHeader?: boolean;
};

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const FlockProfileCard: React.FC<Props> = ({
  model,
  chartMode = 'responsive',
  desktopChartWidth,
  showHeader = true,
}) => (
  <Card className={`sg-flock-profile-card${showHeader ? '' : ' sg-flock-profile-card--no-header'}`}>
    {showHeader && (
      <div className="sg-flock-profile-card__header">
        <div>
          <div className="sg-flock-profile-card__code">{model.flockCode}</div>
          <div className="sg-flock-profile-card__name">{model.flockName}</div>
        </div>

        <div className="sg-flock-profile-card__meta">
          <div>Analysis name: {model.analysisName}</div>
          <div>Analysis date: {formatDate(model.analysisDate)}</div>
        </div>
      </div>
    )}

    <ResponsiveChart model={model} chartMode={chartMode} desktopChartWidth={desktopChartWidth} />
  </Card>
);

const ResponsiveChart: React.FC<Props> = ({ model, chartMode = 'responsive', desktopChartWidth }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const resolvedContainerWidth = chartMode === 'desktop'
    ? desktopChartWidth || Math.max(containerWidth, 900)
    : containerWidth;
  const isMobile = chartMode === 'responsive' && containerWidth > 0 && containerWidth <= 842;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
    };

    updateWidth();

    if (typeof window.ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);

      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new window.ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="sg-flock-profile-card__chart">
      {/* {JSON.stringify(model.traits, null, 2)} */}
      {isMobile ? (
        <FlockProfileChartMobile model={model} />
      ) : (
        <FlockProfileChart model={model} containerWidth={resolvedContainerWidth} />
      )}
    </div>
  );
};
